import React, { useState, useEffect } from 'react';
import { db, storage } from '../../firebaseConfig';
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
  query,
  where,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useUser } from '../../contexts/UserContext';
import { useTranslation } from 'react-i18next';
import './ManageClinics.css';
import BackToClinicDashboard from '../BackToClinicDashboard';
import { useParams } from 'react-router-dom';

const ManageClinics = () => {
  const context = useUser();
  const user = context?.user;
  const { t } = useTranslation();
  const { token } = useParams();

  const [clinics, setClinics] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);

  const [clinicData, setClinicData] = useState({
    name: '',
    location: '',
    address: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    public_listing: false,
    is_active: true,
    barcodeFile: null,
    discount_code: '',
    admin_pin: '',
  });

  const fetchClinics = async () => {
    const q = query(collection(db, 'clinics'), where('is_active', '==', true));
    const snapshot = await getDocs(q);
    const result = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
    setClinics(result);
  };

  useEffect(() => {
    if (user?.role === 'system_admin') {
      fetchClinics();
    }
  }, [user]);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setClinicData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleFileChange = e => {
    setClinicData(prev => ({
      ...prev,
      barcodeFile: e.target.files[0],
    }));
  };

  const handleEdit = clinic => {
    setEditingId(clinic.id);
    setClinicData({
      name: clinic.name,
      location: clinic.location,
      address: clinic.address,
      contact_name: clinic.contact_name,
      contact_email: clinic.contact_email,
      contact_phone: clinic.contact_phone,
      public_listing: clinic.public_listing || false,
      is_active: clinic.is_active !== false,
      barcodeFile: null,
      discount_code: clinic.discount_code || '',
      admin_pin: clinic.admin_pin || '',
    });
  };

  const exportToCSV = () => {
    if (!clinics.length) return;

    const headers = [
      'Name',
      'Location',
      'Address',
      'Contact Name',
      'Contact Email',
      'Contact Phone',
      'Is Active',
      'Public Listing',
      'Unique Code',
      'Token',
      'Created At',
      'Barcode URL',
      'Discount Code',
      'Admin PIN',
    ];

    const rows = clinics.map(clinic => [
      clinic.name || '',
      clinic.location || '',
      clinic.address || '',
      clinic.contact_name || '',
      clinic.contact_email || '',
      clinic.contact_phone || '',
      clinic.is_active ? 'Yes' : 'No',
      clinic.public_listing ? 'Yes' : 'No',
      clinic.unique_code || '',
      clinic.token || '',
      clinic.created_at?.toDate?.().toLocaleString?.() || '',
      clinic.barcode_url || '',
      clinic.discount_code || '',
      clinic.admin_pin || '',
    ]);

    const csvContent = [headers, ...rows]
      .map(e => e.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'clinics_export.csv';
    link.click();
  };

  const handleDelete = async id => {
    try {
      await deleteDoc(doc(db, 'clinics', id));
      await fetchClinics();
    } catch (error) {
      console.error('Failed to delete clinic:', error);
    }
  };

  const generateToken = () => 'clinic-' + Math.random().toString(36).substring(2, 10);

  const handleSubmit = async e => {
    e.preventDefault();

    const {
      name,
      location,
      address,
      contact_name,
      contact_email,
      contact_phone,
      is_active,
      public_listing,
      barcodeFile,
      discount_code,
      admin_pin,
    } = clinicData;

    if (!name || !location) {
      alert(t('manageClinics.fillAllFields'));
      return;
    }

    setLoading(true);

    try {
      let barcodeURL = '';

      if (barcodeFile) {
        if (barcodeFile.size === 0 || barcodeFile.size > 5 * 1024 * 1024) {
          alert('Please upload a valid image under 5MB.');
          setLoading(false);
          return;
        }

        const safeFileName = barcodeFile.name.replace(/[^a-zA-Z0-9.\-_]/g, '_');
        const fileRef = ref(storage, `clinic_barcodes/${Date.now()}_${safeFileName}`);

        try {
          await uploadBytes(fileRef, barcodeFile);
          barcodeURL = await getDownloadURL(fileRef);
        } catch (uploadErr) {
          console.error('Image upload failed:', uploadErr);
          alert('Image upload failed. Please try a different image.');
          setLoading(false);
          return;
        }
      }

      const clinicPayload = {
        name,
        location,
        address,
        contact_name,
        contact_email,
        contact_phone,
        is_active,
        public_listing,
        discount_code,
        admin_pin,
      };

      if (barcodeURL) clinicPayload.barcode_url = barcodeURL;

      if (editingId) {
        await updateDoc(doc(db, 'clinics', editingId), clinicPayload);
        setEditingId(null);
      } else {
        clinicPayload.unique_code = 'CLINIC-' + Math.random().toString(36).substring(2, 10).toUpperCase();
        clinicPayload.token = generateToken();
        clinicPayload.created_at = Timestamp.now();
        await addDoc(collection(db, 'clinics'), clinicPayload);
      }

      setClinicData({
        name: '',
        location: '',
        address: '',
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        is_active: true,
        public_listing: false,
        barcodeFile: null,
        discount_code: '',
        admin_pin: '',
      });

      await fetchClinics();
    } catch (err) {
      console.error('Error saving clinic:', err);
    }

    setLoading(false);
  };

  if (!user || user?.role !== 'system_admin') return null;

  return (
    <div className="manage-clinics-container">
      <h2 className="manage-clinics-title">{t('manageClinics.title')}</h2>
      <form onSubmit={handleSubmit} className="manage-clinics-form">
        <div className="form-grid">
          <input name="name" placeholder={t('manageClinics.name')} value={clinicData.name} onChange={handleChange} />
          <input name="location" placeholder={t('manageClinics.location')} value={clinicData.location} onChange={handleChange} />
          <input name="address" placeholder={t('manageClinics.address')} value={clinicData.address} onChange={handleChange} />
          <input name="contact_name" placeholder={t('manageClinics.contactName')} value={clinicData.contact_name} onChange={handleChange} />
          <input name="contact_email" placeholder={t('manageClinics.contactEmail')} value={clinicData.contact_email} onChange={handleChange} />
          <input name="contact_phone" placeholder={t('manageClinics.contactPhone')} value={clinicData.contact_phone} onChange={handleChange} />
          <input name="discount_code" placeholder={t('manageClinics.discountCode')} value={clinicData.discount_code} onChange={handleChange} className="highlighted-discount" />
          <input name="admin_pin" placeholder={t('manageClinics.adminPin')} value={clinicData.admin_pin} onChange={handleChange} />
        </div>
        <input type="file" accept="image/*" onChange={handleFileChange} />

        <label className="checkbox">
          <input type="checkbox" name="is_active" checked={clinicData.is_active} onChange={handleChange} />
          {t('manageClinics.isActive')}
        </label>
        <label className="checkbox">
          <input type="checkbox" name="public_listing" checked={clinicData.public_listing} onChange={handleChange} />
          {t('manageClinics.publicListing')}
        </label>
        <button type="submit" className="manage-clinics-submit-btn" disabled={loading}>
          {loading ? t('manageClinics.saving') : editingId ? t('manageClinics.update') : t('manageClinics.save')}
        </button>
      </form>

      <h3 className="manage-clinics-title">{t('manageClinics.clinicList')}</h3>
      <div className="text-right my-4">
        <button onClick={exportToCSV} className="copy-link-button">
          {t('manageClinics.exportCsv')}
        </button>
        <BackToClinicDashboard token={token} />
      </div>
      <div className="clinic-list">
        {clinics.map(clinic => (
          <div key={clinic.id} className="clinic-card">
            <div className="clinic-card-info">
              <p><strong>{clinic.name}</strong> ({clinic.unique_code})</p>
              <p>{clinic.location} - {clinic.address}</p>
              {clinic.token && (
                <div className="clinic-links">
                  <p>
                    <strong>{t('manageClinics.link')}:</strong>{' '}
                    <a href={`${window.location.origin}/clinic-dashboard/${clinic.token}`} target="_blank" rel="noreferrer">
                      {t('manageClinics.openLink')}
                    </a>
                  </p>
                  <button
                    className="copy-link-button"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/clinic-dashboard/${clinic.token}`);
                      alert(t('manageClinics.linkCopied'));
                    }}
                  >
                    📋 {t('manageClinics.copyLink')}
                  </button>
                </div>
              )}

              {clinic.discount_code && (
                <div
                  className="discount-code-badge"
                  onClick={() => {
                    navigator.clipboard.writeText(clinic.discount_code);
                    alert(t('manageClinics.codeCopied'));
                  }}
                >
                  {clinic.discount_code} <span className="copy-hint">📋</span>
                </div>
              )}
              <p>{clinic.contact_name} | {clinic.contact_email} | {clinic.contact_phone}</p>
              <p className={`clinic-status-badge ${clinic.is_active ? 'active' : 'inactive'}`}>
                {clinic.is_active ? '✅ ' + t('manageClinics.active') : '❌ ' + t('manageClinics.inactive')}
              </p>
              <p className={`clinic-status-badge ${clinic.public_listing ? 'active' : 'inactive'}`}>
                {clinic.public_listing ? '🌍 ' + t('manageClinics.listedPublicly') : '🙈 ' + t('manageClinics.notListed')}
              </p>
            </div>
            <div className="clinic-card-actions">
              {clinic.barcode_url && (<img src={clinic.barcode_url} alt="Barcode" className="clinic-barcode-img" />)}
              <button onClick={() => handleEdit(clinic)} className="edit-button">
                {t('manageClinics.edit')}
              </button>
              <button onClick={() => handleDelete(clinic.id)} className="delete-button">
                {t('manageClinics.delete')}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ManageClinics;
